<script lang="ts" setup>
import type { CartItem } from '~/types/Cart';

const props = defineProps<{ cartItem: CartItem; isError?: boolean; isUnavailable?: boolean }>();
const cartStore = useCartStore();
const mappedCartItem = computed(() => cartStore.mappedCartItems.find((item) => item.id === props.cartItem.id));
const { isMobile } = useBreakpoints();
const { isCartLoaded, selectedCartItems } = storeToRefs(useCartStore());

const offerSize = computed(() => {
  return props.cartItem.data.displayable_offer_properties.find((prop) => prop.name === 'Размер')?.value ?? '';
});

const isLoading = ref('');
const discountedCartItemData = ref({ price: 0, old_price: 0 });

watch(
  () => cartStore.cart.calculate,
  () => {
    discountedCartItemData.value = { price: 0, old_price: 0 };
    const discountedItem = cartStore.cart.calculate.discount_data.find((item) => item.offer_id === props.cartItem.id);

    if (
      discountedItem?.offers_discounted_price &&
      discountedItem?.offers_discounted_price !== discountedItem?.offers_price
    ) {
      discountedCartItemData.value.old_price = discountedItem?.offers_old_price || discountedItem?.offers_price;
      discountedCartItemData.value.price = discountedItem?.offers_discounted_price;
    }
  },
  {
    immediate: true,
    deep: true,
  },
);

const { sendOfferEventToDataLayer } = useGtmModule();

async function incrementCountInCart() {
  if (isLoading.value) return;

  isLoading.value = 'increment';

  await cartStore.increment(props.cartItem.id);
  await cartStore.getCart();

  isLoading.value = '';
}

async function decrementCountInCart() {
  if (isLoading.value) return;

  isLoading.value = 'decrement';

  await cartStore.decrement(props.cartItem.id);
  await cartStore.getCart();

  const currentItemInCart = cartStore.mappedCartItems.find((item) => item.id === props.cartItem.id);

  if (!currentItemInCart) {
    await sendOfferEventToDataLayer('remove_from_cart', props.cartItem.data);
    window.roistat?.event?.send('remove_from_cart');
  }

  isLoading.value = '';
}

function handleRemovedFromCart() {
  cartStore.getCart();

  window.roistat?.event?.send('remove_from_cart');
  if (window.r46) window.r46('track', 'remove_from_cart', props.cartItem.id);
  sendOfferEventToDataLayer('remove_from_cart', props.cartItem.data);
}

async function handleImageClick() {
  if (isMobile.value) {
    if (cartStore.selectedCartItems.includes(props.cartItem)) {
      cartStore.selectedCartItems = cartStore.selectedCartItems.filter((item) => item.id !== props.cartItem.id);
    } else {
      cartStore.selectedCartItems = [...cartStore.selectedCartItems, props.cartItem];
    }
    await handleCheckboxChange();
  }
}

async function handleCheckboxChange() {
  await cartStore.changeSelectedCartItems([props.cartItem.id], props.cartItem.is_selected);
}

async function replaceUnavailableItem() {
  await cartStore.removeFromCart([props.cartItem.id]);
  await navigateTo({ path: makeUrlRelative(props.cartItem.category_url) });
}
</script>

<template>
  <CardBlock
    v-if="cartItem.data"
    :class="{ 'cart-list-item--error': isError }"
    class="cart-list-item"
  >
    <PrimeCheckbox
      v-if="isMobile === false && !isUnavailable"
      v-model="selectedCartItems"
      :disabled="!isCartLoaded"
      :value="cartItem"
      @change="handleCheckboxChange"
    />

    <component
      :is="isMobile ? 'div' : 'a'"
      :class="{ 'cart-list-item--unavailable': isUnavailable }"
      :href="cartItem.data.uri"
      class="cart-list-item__image-box"
      @click="handleImageClick"
    >
      <PrimeCheckbox
        v-if="isMobile && !isUnavailable"
        v-model="selectedCartItems"
        :disabled="!isCartLoaded"
        :value="cartItem"
        class="cart-list-item__mobile-checkbox"
      />

      <picture v-if="cartItem.data.offer_images.length && cartItem.data.offer_images[0].url">
        <source
          v-if="cartItem.data.offer_images[0].conversions_webp.thumb_url_180"
          :srcset="`${cartItem.data.offer_images[0].conversions_webp.thumb_url_180}, ${cartItem.data.offer_images[0].conversions_webp.thumb_url_360} 2x`"
          media="(max-width: 360px)"
          type="image/webp"
        />

        <source
          v-if="cartItem.data.offer_images[0].conversions_webp.thumb_url_360"
          :srcset="`${cartItem.data.offer_images[0].conversions_webp.thumb_url_360}, ${cartItem.data.offer_images[0].conversions_webp.thumb_url_740} 2x`"
          type="image/webp"
        />

        <source
          v-if="cartItem.data.offer_images[0].conversions.thumb_url_360"
          :srcset="cartItem.data.offer_images[0].conversions.thumb_url_360"
          type="image/jpeg"
        />

        <img
          :alt="cartItem.data.product_name_medium"
          :src="cartItem.data.offer_images[0].url"
          class="cart-list-item__image"
          loading="lazy"
        />
      </picture>

      <SvgoNoPhotoSmall
        v-else
        :font-controlled="false"
        class="w-6 h-6"
        filled
      />
    </component>

    <AppLink
      :class="{ 'cart-list-item--unavailable': isUnavailable }"
      :to="cartItem.data.uri"
      class="cart-list-item__name"
    >
      <div class="flex gap-1.5 items-end">
        <div class="cart-list-item__price">
          <template v-if="discountedCartItemData.old_price">
            <div class="line-through text-xs text-hell-black-60 md:order-2">
              {{ discountedCartItemData.old_price.toLocaleString('ru-RU') }} ₽
            </div>

            <div class="font-bold sm:order-1 leading-4">
              {{ discountedCartItemData.price.toLocaleString('ru-RU') }} ₽
            </div>
          </template>

          <template v-else>
            <div
              v-if="cartItem.data.old_price"
              class="line-through text-xs text-hell-black-60 md:order-2"
            >
              {{ (cartItem.data.old_price * cartItem.count).toLocaleString('ru-RU') }} ₽
            </div>

            <div class="font-bold sm:order-1 leading-4">
              {{ (cartItem.data.price * cartItem.count).toLocaleString('ru-RU') }} ₽
            </div>
          </template>
        </div>

        <LoyaltyBadge
          v-if="!isUnavailable"
          :amount="cartItem.data.loyalty"
        />
      </div>

      <div>{{ cartItem.data.product_name_medium }}</div>

      <div
        v-if="offerSize"
        class="text-xs text-hell-black-40"
      >
        Размер {{ offerSize }}
      </div>
    </AppLink>

    <div
      v-if="isUnavailable"
      class="flex flex-col gap-2"
    >
      <p class="text-hell-yellow text-3xs sm:text-2xs">Разобрали,&nbsp;есть&nbsp;похожие</p>

      <TheBtn
        color="outline"
        size="sm"
        @click="replaceUnavailableItem"
      >
        Заменить
      </TheBtn>
    </div>

    <div
      v-else
      class="cart-list-item__counter"
    >
      <p
        v-if="isError"
        class="absolute -top-5 md:-top-6 text-hell-red text-2xs md:text-xs"
      >
        В&nbsp;наличии&nbsp;{{ cartItem.available_count }}
      </p>

      <ProductCardCounterBlock
        :count-in-cart="mappedCartItem?.count"
        :is-loading="isLoading"
        :is-not-available-to-increment="cartItem.count >= cartItem.available_count"
        bordered
        @decrement="decrementCountInCart"
        @increment="incrementCountInCart"
      />

      <p
        v-if="cartItem.count > 1"
        class="absolute -bottom-5 md:-bottom-6 text-hell-black-60 text-2xs md:text-xs"
      >
        {{ cartItem.data.price }}&nbsp;за&nbsp;шт.
      </p>
    </div>

    <div class="flex flex-col gap-2 h-full justify-between">
      <FavoriteBtn :offer="cartItem.data" />

      <RemoveFromCartBtn
        :offer-id="cartItem.id"
        @removed-from-cart="handleRemovedFromCart"
      />
    </div>
  </CardBlock>
</template>

<style lang="scss">
.cart-list-item {
  display: flex;
  gap: 12px;
  align-items: center;

  &--error {
    border: 1px solid $hell-red !important;
  }

  &--unavailable {
    opacity: 0.7;
  }

  &__image-box {
    position: relative;
    display: flex;
    flex-shrink: 0;
    justify-content: center;
    align-items: center;
    width: 50px;
    height: 50px;
    border-radius: 12px;
    cursor: pointer;

    @include screen-md {
      width: 80px;
      height: 80px;
    }
  }

  &__mobile-checkbox {
    position: absolute;
    top: 0;
    left: 0;

    &.p-checkbox .p-checkbox-box {
      width: 14px;
      height: 14px;
      border: 1px solid $hell-black-20;
      box-shadow: none;
    }

    @include screen-md {
      position: relative;
    }
  }

  &__image {
    object-fit: contain;
    width: 50px;
    height: 50px;
    border-radius: 12px;

    @include screen-md {
      width: 80px;
      height: 80px;
    }
  }

  &__name {
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    gap: 4px;
  }

  &__counter {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: center;

    @include screen-md {
      gap: 8px;
      margin-right: 28px;
    }
  }

  &__price {
    display: flex;
    flex-direction: column;
    gap: 2px;
    justify-content: center;

    @include screen-sm {
      flex-direction: row;
      gap: 8px;
      align-items: center;
    }
  }
}
</style>
